import { DataGridConfig } from 'components/DataGrid';
import { useAtom, useAtomValue } from 'jotai';
import callApi, { isCancellationResponse } from 'lib/callApi';
import { formatDate, isAdminUserLoggedIn } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import { useEffect, useMemo, useState } from 'react';

import RowActions from './RowActions';
import {
  membersDataAtom,
  orderbyAtom,
  paginationConfigAtom,
  sortOrderAtom,
  selectedRowIdsAtom,
  forceRerenderMembersGridAtom,
  membersDataQueryParamsAtom,
} from './state';

/**
 * Custom hook for configuring the Members DataGrid
 * Handles data fetching, sorting, pagination, and row selection
 * @returns Configuration object for the DataGrid component
 */
export const useMembersDataConfig = () => {
  const dataQueryParams = useAtomValue(membersDataQueryParamsAtom);
  const [listingData, setListingData] = useAtom(membersDataAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const forceRenderGrid = useAtomValue(forceRerenderMembersGridAtom);

  const [loading, setLoading] = useState(false);

  const enableSelectionAcrossPages = true;

  useEffect(() => {
    // we are making the API call here, to get the loading screen on data grid
    // if we do it in atoms then the loading screen will not be shown
    setLoading(true);
    const fetchData = async () => {
      if (dataQueryParams === null) return;
      try {
        setListingData({ ...listingData, loading: true });
        const responseData = await callApi(
          `/profiles/colleague/new/`,
          'GET',
          dataQueryParams,
        );

        if (isCancellationResponse(responseData)) {
          console.log('Request was cancelled');
          return;
        }

        setListingData({ ...responseData?.data, loading: false });
      } catch (error) {
        // eslint-disable-next-line no-debugger, @typescript-eslint/no-explicit-any
        const errorData = (error as any)?.data;
        const errorMessage = errorData
          ? `${Object.keys(errorData)[0]}: ${
              Object.values(errorData).flat()[0]
            }`
          : 'Failed to load data.';
        showFailureToast(errorMessage);
        setListingData({ results: [], count: 0, loading: false });
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [dataQueryParams]);

  const caclculateDaysDifference = (date: string) => {
    const createdAt = new Date(date);
    const currentDate = new Date();

    createdAt.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);

    const msPerDay = 1000 * 60 * 60 * 24;
    const timeDifference = currentDate.getTime() - createdAt.getTime();
    const daysDifference = Math.floor(timeDifference / msPerDay);
    return daysDifference;
  };

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'users_id',
      columns: [
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          headerTooltip: 'Member name',
          enableCopyToClipboard: true,
        },
        {
          key: 'email',
          label: 'Email',
          sortable: true,
          headerTooltip: 'Member email address',
          enableCopyToClipboard: true,
        },
        {
          key: 'company_role',
          label: 'Role',
          sortable: true,
          headerTooltip: 'Member role in the company',
        },
        {
          key: 'confirmation_token_send_at',
          label: 'Invited On',
          sortable: true,
          headerTooltip: 'Date when the member was invited',
          classRules: [
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const daysDifference = caclculateDaysDifference(
                  rowData.confirmation_token_send_at,
                );
                return (
                  !rowData.is_active &&
                  daysDifference > 7 &&
                  daysDifference < 14
                );
              },
            },
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const daysDifference = caclculateDaysDifference(
                  rowData.confirmation_token_send_at,
                );
                return !rowData.is_active && daysDifference >= 14;
              },
            },
          ],
          tooltip: (rowData) => {
            const daysDifference = caclculateDaysDifference(
              rowData.confirmation_token_send_at,
            );
            if (rowData.is_active) {
              return null;
            }
            return daysDifference > 0
              ? `Invited ${daysDifference} day${
                  daysDifference > 1 ? 's' : ''
                } ago`
              : 'Invited today';
          },
        },
        {
          key: 'is_active',
          label: 'Active',
          sortable: true,
          headerTooltip: 'Whether the member has confirmed their account',
          overrideContents: (rowData) => (
            <span>{rowData.is_active ? 'Yes' : 'No'}</span>
          ),
          classRules: [
            {
              className: 'text-medium',
              ruleFunction: (rowData) => {
                const daysDifference = caclculateDaysDifference(
                  rowData.confirmation_token_send_at,
                );
                return (
                  !rowData.is_active &&
                  daysDifference > 7 &&
                  daysDifference < 14
                );
              },
            },
            {
              className: 'text-critical',
              ruleFunction: (rowData) => {
                const daysDifference = caclculateDaysDifference(
                  rowData.confirmation_token_send_at,
                );
                return !rowData.is_active && daysDifference >= 14;
              },
            },
          ],
          tooltip: (rowData) => {
            return (
              !rowData.is_active &&
              'Inactive users will not receive subscription reports'
            );
          },
        },
        {
          key: 'subscriptions',
          label: 'Subscriptions',
          sortable: false,
          headerTooltip: 'Subscribed Reports',
          overrideContents: (rowData) => {
            // Get active subscriptions and their report names
            const activeSubscriptions = Object.keys(rowData.subscriptions)
              .filter((key) => rowData.subscriptions[key].subscribed)
              .map((key) => rowData.subscriptions[key].report_type);

            return (
              <div>
                {activeSubscriptions.length > 0 ? (
                  <span>{activeSubscriptions.join(', ')}</span>
                ) : null}
              </div>
            );
          },
          tooltip: (rowData) => {
            // Check if there are any active subscriptions
            const hasActiveSubscriptions =
              rowData.subscriptions &&
              Object.keys(rowData.subscriptions).some(
                (key) =>
                  rowData.subscriptions[key] &&
                  rowData.subscriptions[key].subscribed,
              );

            return (
              <div className="p-1">
                {rowData.subscriptions &&
                Object.keys(rowData.subscriptions).length > 0 ? (
                  <div>
                    <div className="tooltip-header mb-3 pb-2 border-bottom">
                      <div className="d-flex align-items-center">
                        <i
                          className={`fa ${
                            hasActiveSubscriptions
                              ? 'fa-bell text-primary'
                              : 'fa-bell-slash text-muted'
                          } mr-2`}
                        ></i>
                        <strong>
                          {hasActiveSubscriptions
                            ? 'Active Subscriptions'
                            : 'Available Subscriptions'}
                        </strong>
                      </div>
                      {!hasActiveSubscriptions && (
                        <div className="small text-muted mt-1">
                          No active subscriptions found
                        </div>
                      )}
                    </div>
                    <div className="subscription-list">
                      {Object.keys(rowData.subscriptions).map((key) => {
                        const subscription = rowData.subscriptions[key];
                        return (
                          <div
                            key={key}
                            className={`subscription-item d-flex align-items-center mb-3 ${
                              !subscription.subscribed ? 'opacity-50' : ''
                            }`}
                          >
                            <div
                              className={`subscription-status-indicator mr-2 ${
                                subscription.subscribed
                                  ? 'bg-primary'
                                  : 'bg-secondary'
                              }`}
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                              }}
                            ></div>
                            <div className="subscription-icon mr-2">
                              <i
                                className={`fa ${
                                  key.includes('webapp')
                                    ? 'fa-globe'
                                    : 'fa-shield-alt'
                                } ${
                                  subscription.subscribed
                                    ? 'text-primary'
                                    : 'text-muted'
                                }`}
                              ></i>
                            </div>
                            <div className="subscription-details flex-grow-1">
                              <div className="subscription-name d-flex justify-content-between">
                                <span>{subscription.report_type}</span>
                                <span
                                  className={`badge badge-pill ${
                                    subscription.subscribed
                                      ? 'badge-primary'
                                      : 'badge-secondary'
                                  } ml-2`}
                                >
                                  {subscription.subscribed
                                    ? 'Active'
                                    : 'Inactive'}
                                </span>
                              </div>
                              <div className="subscription-date small text-muted">
                                Last Notified:{' '}
                                {subscription.last_notified_at
                                  ? formatDate(
                                      new Date(subscription.last_notified_at),
                                    )
                                  : 'Never'}
                                {subscription.subscribed_by && (
                                  <div>
                                    Updated by: {subscription.subscribed_by}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="tooltip-footer mt-2 pt-2 border-top">
                      <small className="text-muted">
                        <i className="fa fa-info-circle mr-1"></i>
                        Click <i className="fa fa-bell" /> to modify
                        subscriptions
                      </small>
                    </div>
                  </div>
                ) : (
                  <div className="no-subscriptions text-center p-3">
                    <div className="mb-2">
                      <i className="fa fa-bell-slash fa-2x text-muted"></i>
                    </div>
                    <div>No subscriptions available</div>
                    <small className="text-muted">
                      Click <i className="fa fa-bell" /> to add subscriptions
                    </small>
                  </div>
                )}
              </div>
            );
          },
        },
        {
          key: 'actions',
          label: 'Actions',
          render: (rowData) => <RowActions rowData={rowData} />,
          headerTooltip: 'Actions',
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
        },
      ],
      paginationConfig: {
        total_count: listingData?.count || 0,
        pageNumber: paginationConfig.page - 1, // page number expects a zero based index
        pageSize: paginationConfig.page_size,
        onPageChange: (newPage: number) => {
          // page number returns a zero based index, so incrementing by 1
          setPaginationConfig({
            page_size: paginationConfig.page_size,
            page: newPage + 1,
          });

          if (!enableSelectionAcrossPages) {
            // reset selected row ids on  change
            setSelectedRowIds([]);
          }
        },
        onPageSizeChange: (newPageSize: number) => {
          // reset page number on page size change
          setPaginationConfig({ page_size: newPageSize, page: 1 });

          // reset selected row ids on  change
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderby,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value) => setSortOrder(value),
      },
      rowSelectionConfig: isAdminUserLoggedIn()
        ? {
            preSelectedRows: selectedRowIds,
            onSelectionChange: (updatedRowIds) => {
              setSelectedRowIds(updatedRowIds);
            },
            enableSelectionAcrossPages: enableSelectionAcrossPages,
          }
        : undefined,
    }),
    [
      listingData,
      orderby,
      sortOrder,
      paginationConfig,
      selectedRowIds,
      forceRenderGrid,
    ],
  );

  return { config, data: listingData?.results, loading };
};
